import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Alert, Spinner } from '@digitools/honest-react';
import { Breadcrumb, Breadcrumbs } from '@digitools/honest-ui';
import styles from './CoverageDetails.scss';
import { useAuth0, useLanguage, useExperience } from 'status/hooks';
import useGet from '../../../../../hooks/useGet';
import CoverageTabs from './CoverageTabs';
import titleCase from 'status/utilities/titleCase';
import { decode } from 'status/utilities/hash';
import TeamViewEmployeeActions from './TeamViewEmployeeActions';
import EmployeeEligibilityDetails from './EmployeeEligibilityDetails';
import { isTeamView } from '../../../../public/usaa/utils/CustomerUtils';
import Notifications, { FunctionalArea } from '../../../../../components/Notifications/Notifications';
import useTealium from '../../../../../hooks/useTealium';
import { TEALIUM_PAGE_VIEW } from '../../../../../constants/tealiumConstants';
import { withRouter } from 'src/utils/withRouter';
import queryString from 'query-string';
import { useHomePageBreadcrumbs } from 'src/components/Breadcrumbs';
import BreadcrumbText from 'src/packages/status/i18n/BreadcrumbText';
import useTranslate from 'src/hooks/useTranslate';
const Error = () => (<Alert type={'error'}>
    <span>
      <strong>Error!</strong>&nbsp;Something went wrong when trying to load the employee.
    </span>
  </Alert>);
// Helper function to determine if we can show EOI
const showEOI = userMeta => {
    const applicationPermissions = userMeta.applicationPermissions;
    return applicationPermissions.indexOf('statusEE.eoiStatus') > -1;
};
/**
 * Backstory: Sometimes, employees will only have an empId
 * because they don't exits in S1. An user might want to
 * view details on this employee but all they have to go on is
 * the empId. Therefore, we need a way for users to view
 * this data ( coverage details ) without an empNum. This
 * is where PartialCoverageDetails comes into play. Its
 * a component that will render details based on the empId.
 *
 * Updated 10/16 - no longer routing EE users without GP or S1 empNum to this page
 * They will instead be redirected to findMyInfo
 */
const PartialCoverageDetails = ({ fromTeamView }) => {
    const location = useLocation();
    const { userMeta } = useAuth0();
    const { language } = useLanguage();
    const { experience, getExperience } = useExperience();
    const { trackView } = useTealium();
    const params = queryString.parse(location.search);
    const { viewStatus } = BreadcrumbText;
    const { t } = useTranslate();
    const { empNum } = useParams();
    useEffect(() => {
        trackView({
            page_l3: TEALIUM_PAGE_VIEW.PAGE_L3.STATUS,
            page_l4: experience === 'ER'
                ? TEALIUM_PAGE_VIEW.PAGE_L4.STATUS.EMPLOYER_VIEW
                : TEALIUM_PAGE_VIEW.PAGE_L4.STATUS.EMPLOYEE_VIEW,
            page_l5: TEALIUM_PAGE_VIEW.PAGE_L5.STATUS.COVERAGE_DETAILS
        });
    }, []);
    // When routing to a page based on empId its encoded as "id-${theid}"
    const empId = decode(empNum).split('-')[1];
    const { error: employeeError, loading: loadingEmployee, data: employeeData, } = useGet({
        url: `/mlp-ui-service/api/employees/hierarchy/${empId}`,
        // Currently we CANT hit this endpoint as employee that has no empnum
        // Why? because its restricted to allow ERs and EE's whos empNum in token match
        // Emp num of the employee being retrieved
        //
        // Scenario: SSO EE user wants to view data via emp ID ( Unplanned absences, EOI )
        //
        // So, we must prevent this call for EE users that do not have empNum
        // Because this page will only be rendered for EE users that dont have emp num
        // we can simply check to see if the experience is EE and not make the call
        //
        // Why do we make this call in thef first place?
        // Because we need to be able to show employee data on this screen for ER users
        noInitial: getExperience() === 'EE',
        options: {
            headers: {
                Accept: 'application/hal+json',
            },
        },
    });
    const showEOITab = showEOI(userMeta);
    const { error: eoiError, loading: loadingEoi, data: eoiData, } = useGet({
        url: '/status-service/eoi/status',
        options: {
            params: {
                languageCode: language,
            },
        },
        noInitial: !showEOITab,
    }, [language]);
    const loading = loadingEoi || loadingEmployee;
    if (loading) {
        return <Spinner id='partial-coverage-details-spinner'/>;
    }
    if (employeeError) {
        return <Error />;
    }
    let name;
    // IF we are ER we need to make sure we got employee
    if (experience === 'ER') {
        const employee = employeeData.emp;
        if (!employee) {
            return <Error />;
        }
        name = `${titleCase(employee.firstName)} ${titleCase(employee.lastName)}`;
    }
    else {
        name = `${titleCase(userMeta.firstName)} ${titleCase(userMeta.lastName)}`;
    }
    const teamView = isTeamView(userMeta);
    return (<div className={styles.container}>
      <Notifications functionalArea={FunctionalArea.COVERAGE_DETAILS}/>
      {fromTeamView && (<Breadcrumbs>
          {useHomePageBreadcrumbs()}
          <Breadcrumb href={params.search && params.searchCriteria ?
                `/teamview?search=${params?.search}&searchCriteria=${params?.searchCriteria}` :
                '/teamview'}>
            Team view
          </Breadcrumb>
          <Breadcrumb>{t(viewStatus)}</Breadcrumb>
        </Breadcrumbs>)}
      {/* @ts-ignore */}
      <EmployeeEligibilityDetails empId={empId}/>
      {fromTeamView ? <TeamViewEmployeeActions empId={empId}/> : null}
      {/* @ts-ignore */}
      <CoverageTabs eoiData={eoiData} eoiError={eoiError} showEOITab={showEOITab} empId={empId} fromTeamView={fromTeamView}/>
    </div>);
};
export default withRouter(PartialCoverageDetails);
